import { notification_color_keys, useNotifications } from "utils/hooks";
import { is_empty } from "utils/validations";
import React from "react";
import { logError } from "utils/error";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { refreshIframe, validateData } from "../../Utils";
import { api, dataProvider } from "data";
import { apiMethods } from "data/api.constants";
import { isRequestSuccessful } from "@my-scoot/exly-react-component-lib";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import { FORM_KEYS, SECTION_SIDE_KEYS } from "../CustomFooter.constants";
import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";

export const useCustomFooter = () => {
  const { notify } = useNotifications();
  const { miscellaneous_data, fetchMiscellaneousData, set_miscellaneous_data } =
    React.useContext(WebpageCreationContext);

  React.useEffect(() => {
    assignData();
  }, [miscellaneous_data]);

  const [data, set_data] = React.useState({});
  const [uuid, set_uuid] = React.useState(null);
  const [show_modal, set_show_modal] = React.useState(false);
  const [selected_record, set_selected_record] = React.useState({});
  const [error_object, set_error_object] = React.useState({});
  const [processing, set_processing] = React.useState(false);
  const [previewData, setPreviewData] = React.useState({});

  React.useEffect(() => {
    // update preview data only when modal is closed so that preview doesnt change onChange of modal fields
    if (!show_modal && JSON.stringify(data) !== JSON.stringify(previewData)) {
      setPreviewData(JSON.parse(JSON.stringify(data)));
    }
  }, [data, show_modal]);

  const assignData = async () => {
    try {
      if (is_empty(miscellaneous_data?.customFooter)) return;
      let [temp] = miscellaneous_data?.customFooter;
      set_data(temp?.data);
      set_uuid(temp?.uuid);
    } catch (error) {
      logError({
        error,
        when: "assignData",
        occuredAt:
          "src/ui/pages/TemplatePreview/components/Sidebar/components/CreatorMiscellaneousData/CustomFooter/index.jsx",
      });
      notify(
        error?.body?.message ?? SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
    }
  };

  const handleSave = async ({ section_side }) => {
    try {
      if (processing) return;
      set_processing(true);

      if (!is_empty(selected_record)) {
        const selected_data = { ...data[section_side][selected_record.id] };
        if (!is_empty(selected_data)) delete selected_data[FORM_KEYS.link.key]; // skip null check for link
        const error_obj = validateData(selected_data);
        const check_errors = Object.values(error_object).filter(
          (value) => value === true
        );
        if (!is_empty(error_obj) || !is_empty(check_errors)) {
          set_error_object(Object.assign(error_object, error_obj));
          return notify("Invalid form values", notification_color_keys.error);
        }
      }

      let payload, api_resource;
      let temp = data;
      if (temp?.[section_side]?.[selected_record.id]?.draft) {
        delete temp[section_side][selected_record.id].draft;
      }
      if (is_empty(uuid)) {
        api_resource = api.creator_miscellaneous_data.create;
        payload = {
          type: creator_miscellaneous_data_types.CUSTOM_FOOTER,
          data: temp,
        };
      } else {
        api_resource = api.creator_miscellaneous_data.update;
        payload = {
          uuid: uuid,
          updated_values: {
            data: temp,
          },
        };
      }
      const status = await dataProvider.custom_request(
        api_resource,
        apiMethods.POST,
        payload
      );
      if (isRequestSuccessful(status.status)) {
        refreshIframe();
        set_show_modal(false);
        set_selected_record({});
        set_data({ ...temp });

        if (is_empty(uuid)) {
          fetchMiscellaneousData({
            type: creator_miscellaneous_data_types.CUSTOM_FOOTER,
          });
        } else {
          let context_data = miscellaneous_data.customFooter;
          context_data[0].data = temp;
          set_miscellaneous_data({
            ...miscellaneous_data,
            customFooter: context_data,
          });
        }
      }
      notify(status?.message);
    } catch (error) {
      logError({
        error,
        when: "handleSave",
        occuredAt:
          "src/ui/pages/TemplatePreview/components/Sidebar/components/CreatorMiscellaneousData/CustomFooter/utils/useCustomFooter.js",
      });
      notify(
        error?.body?.message ?? SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
    } finally {
      set_processing(false);
    }
  };

  const handleDelete = () => {
    let temp = data;
    temp[selected_record.section_side].splice(parseInt(selected_record.id), 1);
    for (let i = 0; i < temp[selected_record.section_side].length; i++) {
      temp[selected_record.section_side][i].id = i;
    }
    set_data({ ...temp });
    handleSave({ section_side: selected_record.section_side });
  };

  const handleModalClose = () => {
    const published_data = getPublishedData(
      JSON.parse(JSON.stringify(previewData))
    );
    set_data({ ...published_data });
    set_error_object({});
    set_selected_record({});
    set_show_modal(false);
  };

  const getPublishedData = (values = data) => {
    // remove draft entries
    // let published_data = data.filter((item) => !item.draft);
    let published_data = {
      ...values,
      [SECTION_SIDE_KEYS.left_section.key]: !is_empty(
        values?.[SECTION_SIDE_KEYS.left_section.key]
      )
        ? values[SECTION_SIDE_KEYS.left_section.key].filter(
            (item) => !item.draft
          )
        : [],
      [SECTION_SIDE_KEYS.right_section.key]: !is_empty(
        values?.[SECTION_SIDE_KEYS.right_section.key]
      )
        ? values[SECTION_SIDE_KEYS.right_section.key].filter(
            (item) => !item.draft
          )
        : [],
    };

    return published_data;
  };

  const addItem = (section_side) => {
    let published_data = getPublishedData();
    let initial_data = {
      id: published_data[section_side].length,
      [FORM_KEYS.link.key]: "",
      [FORM_KEYS.text.key]: "",
      [FORM_KEYS.section_side.key]: section_side,
      draft: true,
    };
    // if changing side; delete record from current side and add that item in new side
    if (!is_empty(selected_record)) {
      published_data[selected_record.section_side].splice(
        parseInt(selected_record.id),
        1
      );
      for (
        let i = 0;
        i < published_data[selected_record.section_side].length;
        i++
      ) {
        published_data[selected_record.section_side][i].id = i;
      }
      initial_data[FORM_KEYS.link.key] = selected_record[FORM_KEYS.link.key];
      initial_data[FORM_KEYS.text.key] = selected_record[FORM_KEYS.text.key];
    }

    set_selected_record(initial_data);
    set_data({
      ...published_data,
      [section_side]: [...published_data[section_side], initial_data],
    });
  };

  const updateData = (value, key, section_side) => {
    let temp = data;
    temp[section_side][selected_record.id][key] = value;
    set_data({ ...temp });
  };

  return {
    data,
    previewData,
    updateData,
    set_show_modal,
    set_data,
    uuid,
    handleDelete,
    selected_record,
    set_selected_record,
    show_modal,
    handleModalClose,
    processing,
    error_object,
    handleSave,
    set_error_object,
    addItem,
  };
};
